import React from 'react';
import MetricsCard from './components/MetricsCard';
import CountrySplitChart from './components/CountrySplitChart';
import MsmeSplitChart from './components/MsmeSplitChart';
import CategorySplitChart from './components/CategorySplitChart';
import CurrencySplitChart from './components/CurrencySplitChart';
import OnboardingTrendsChart from './components/OnboardingTrendsChart';
import RegionSplitChart from './components/RegionSplitChart';
import PaymentTermsSplitChart from './components/PaymentTermsSplitChart';
import SupplierGroupSplitChart from './components/SupplierGroupSplitChart';
import CommodityCodeSplitChart from './components/CommodityCodeSplitChart';
import DeliveryTermsSplitChart from './components/DeliveryTermsSplitChart';
import TurnaroundByStageChart from './components/TurnaroundByStageChart';
import { dashboardService } from '../../services/apiService';
import { useApi } from '../../hooks/useApi';
import { useDashboardSplit } from './hooks/useDashboardSplit';
import { useToast } from '../../components/ui/Toast';
import { SkeletonMetricCard } from '../../components/ui/SkeletonLoader';

// Card surface per spec: flat, bordered, no shadow/hover/tint.
const CARD = 'rounded-md border border-dash-border bg-dash-card p-4';

const DashboardOverview = () => {
  const { error: toastError } = useToast();

  const { data: metrics, loading, error, refetch } = useApi(
    () => dashboardService.getMetrics(),
    {
      onError: (err) => {
        console.error('Dashboard metrics error:', err);
        toastError('Failed to load dashboard metrics. Using cached data.');
      },
    }
  );

  const monthName = new Date().toLocaleString('en-US', { month: 'long' });

  const metricsData = metrics?.data ? [
    { title: 'Total Vendors', value: String(metrics.data.totalVendors?.value || 0), subtitle: 'onboarded to date', color: 'primary' },
    { title: 'Pending Approvals', value: String(metrics.data.pendingApprovals?.value || 0), subtitle: 'awaiting action', color: 'warning' },
    { title: 'Rejected Vendors', value: String(metrics.data.rejectedVendors?.value || 0), subtitle: 'requests declined', color: 'destructive' },
    { title: `${monthName} Onboarded`, value: String(metrics.data.thisMonthOnboarded?.value || 0), subtitle: 'current month', color: 'success' },
  ] : [];

  return (
    <div className="dashboard-theme h-full flex flex-col gap-4 pb-3 min-w-0">
      {/* Error Message with Retry */}
      {error && (
        <div className="rounded-md border border-dash-border bg-dash-card p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-dash-destructive">Error Loading Dashboard</p>
              <p className="text-[11px] text-dash-muted-foreground mt-0.5">{error}</p>
            </div>
            <button
              onClick={refetch}
              className="rounded-md border border-dash-border px-3 py-1.5 text-[11px] font-medium text-dash-foreground"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Row 1: Key Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 flex-shrink-0">
        {loading
          ? Array.from({ length: 4 }).map((_, index) => <SkeletonMetricCard key={index} />)
          : metricsData.map((metric, index) => (
              <MetricsCard
                key={index}
                title={metric.title}
                value={metric.value}
                subtitle={metric.subtitle}
                color={metric.color}
              />
            ))}
      </div>

      {/* Row 2: MSME | MSME Category | Currency */}
      <div className={CARD}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="flex flex-col border-b md:border-b-0 md:border-r border-dash-border pb-3 md:pb-0 md:pr-4">
            <MsmeSplitChart />
          </div>
          <div className="flex flex-col border-b md:border-b-0 lg:border-r border-dash-border pb-3 md:pb-0 lg:pr-4">
            <CategorySplitChart />
          </div>
          <div className="flex flex-col">
            <CurrencySplitChart />
          </div>
        </div>
      </div>

      {/* Row 3: Onboarding Trends (full width) */}
      <div className={CARD}>
        <OnboardingTrendsChart />
      </div>

      {/* Row 4: Region | Country */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={CARD}><RegionSplitChart /></div>
        <div className={CARD}><CountrySplitChart /></div>
      </div>

      {/* Row 5: Payment Terms | Supplier Group */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={CARD}><PaymentTermsSplitChart /></div>
        <div className={CARD}><SupplierGroupSplitChart /></div>
      </div>

      {/* Row 6: Commodity Code | Delivery Terms */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={CARD}><CommodityCodeSplitChart /></div>
        <div className={CARD}><DeliveryTermsSplitChart /></div>
      </div>

      {/* Row 7: Turnaround by Stage (full width) */}
      <div className={CARD}><TurnaroundByStageChart /></div>
    </div>
  );
};

export default DashboardOverview;
